import { supabase } from '@/features/sistema/services/supabaseClient';

import {
  ensureThread,
  fetchThreadComments as apiFetchThreadComments,
  fetchThreadsByObra as apiFetchThreadsByObra,
  getCommentLikes,
  hasUserLiked,
  markAllThreadsRead as apiMarkAllThreadsRead,
  markThreadRead as apiMarkThreadRead,
  markThreadUnread as apiMarkThreadUnread,
  sendThreadComment as apiSendThreadComment,
  setPreferredObras as apiSetPreferredObras,
  toggleCommentLike,
} from '../api/notificationsApi';
import {
  getUnreadCountByObra as apiGetUnreadByObra,
  getUnreadMentionCountByObra as apiGetUnreadMentionCountByObra,
} from '../api/unreadApi';
import {
  persistPreferredObras,
  readPreferredObras,
} from '../utils/notificationPreferences';
const NOTIFICATIONS_TABLE = 'notificacoes';

export function buildUuidArrayFilter(ids: string[]): string {
  const sanitized = ids
    .filter((id): id is string => typeof id === 'string' && id.length > 0)
    .map((id) => `"${id.replace(/"/g, '""')}"`);
  return `{${sanitized.join(',')}}`;
}

export function isNotificationAPISupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window;
}

export function getPermission(): NotificationPermission {
  if (!isNotificationAPISupported()) return 'denied';
  return Notification.permission;
}

export async function requestPermission(): Promise<NotificationPermission> {
  if (!isNotificationAPISupported()) return 'denied';
  const current = Notification.permission;
  if (current !== 'default') return current;
  try {
    return await Notification.requestPermission();
  } catch {
    return Notification.permission;
  }
}

export async function getNotificationPermission(): Promise<NotificationPermission> {
  if (!isNotificationAPISupported()) return 'denied';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission !== 'denied') {
    return requestPermission();
  }
  return 'denied';
}

export function showBrowserNotification(opts: { title: string; body?: string; data?: any; icon?: string }) {
  if (!isNotificationAPISupported()) return;
  new Notification(opts.title, {
    body: opts.body,
    icon: opts.icon || '/icons/icon-192.png',
    data: opts.data,
    tag: 'acor-mention',
  });
}

export async function fetchThreadsByObra(params: {
  obraId: string;
  search?: string | null;
  onlyUnread?: boolean;
  onlyMentions?: boolean;
  limit?: number;
  offset?: number;
}) {
  return apiFetchThreadsByObra(params);
}

export async function fetchThreads(obraId: string, offset = 0, limit = 50) {
  return apiFetchThreadsByObra({ obraId, offset, limit });
}

export async function fetchThreadComments(threadId: string, limit = 50, offset = 0) {
  return apiFetchThreadComments(threadId, limit, offset);
}

export async function ensureThreadExists(params: { obraId?: string | null; recurso: string; recursoId: string }) {
  return ensureThread(params);
}

export async function sendThreadComment(args: {
  threadId: string;
  conteudo: string;
  tipo?: string;
  arquivo_url?: string | null;
  arquivo_nome?: string | null;
  mencoes?: string[] | null;
  comentario_pai_id?: string | null;
  createdBy?: string | null;
  created_by?: string | null;
}) {
  return apiSendThreadComment(args);
}

export async function markThreadAsRead(threadId: string) {
  await apiMarkThreadRead(threadId);
}

export async function markThreadAsUnread(threadId: string, usuarioId?: string | null) {
  await apiMarkThreadUnread(threadId, usuarioId ?? undefined);
}

export const markThreadRead = markThreadAsRead;
export const markThreadUnread = markThreadAsUnread;

export async function markAsRead(threadId: string) {
  return markThreadAsRead(threadId);
}

export async function markAllThreadsRead(obraId?: string | null, options?: { onlyMentions?: boolean }) {
  await apiMarkAllThreadsRead({ obraId: obraId ?? null, onlyMentions: options?.onlyMentions ?? false });
}

export async function getUnreadCountGlobal(usuarioId?: string | null) {
  if (!usuarioId) return 0;
  const { count, error } = await supabase
    .from('v_app_threads_unread')
    .select('id', { head: true, count: 'exact' })
    .eq('usuario_id', usuarioId);

  if (error) {
    console.error('[notificationsService] getUnreadCountGlobal', error);
    return 0;
  }

  return typeof count === 'number' ? count : 0;
}

export async function getUnreadCountByObra(obraId: string, usuarioId: string) {
  return apiGetUnreadByObra(obraId, usuarioId);
}

export const getUnreadByObra = getUnreadCountByObra;

export async function getUnreadMentionCountByObra(obraId: string, usuarioId: string) {
  return apiGetUnreadMentionCountByObra(obraId, usuarioId);
}

export const getUnreadMentionsByObra = getUnreadMentionCountByObra;

export async function fetchUnreadCount(obraId: string, onlyMentions = false) {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user?.id) {
      return { count: 0, error: null } as const;
    }
    const count = onlyMentions
      ? await getUnreadMentionCountByObra(obraId, user.id)
      : await getUnreadCountByObra(obraId, user.id);
    return { count, error: null } as const;
  } catch (error) {
    return { count: 0, error } as const;
  }
}

export async function getPreferredObras() {
  return readPreferredObras();
}

export async function setPreferredObras(obraIds: string[]) {
  await apiSetPreferredObras(obraIds);
  persistPreferredObras(obraIds ?? []);
}

export async function getCommentLikesSnapshot(commentIds: string[]) {
  return getCommentLikes(commentIds);
}

export async function toggleLike(commentId: string, usuarioId: string, shouldLike: boolean) {
  await toggleCommentLike(commentId, usuarioId, shouldLike);
}

export async function hasUserLikedComment(commentId: string, usuarioId: string) {
  return hasUserLiked(commentId, usuarioId);
}

export async function getUnreadThreadsForUser(params: {
  usuarioId: string;
  obraId?: string | null;
  includeMentions?: boolean;
}) {
  const { usuarioId, obraId = null, includeMentions = true } = params;

  let threadsQuery = supabase
    .from('app_comment_threads')
    .select('id,last_at,obra_id')
    .order('last_at', { ascending: false });

  if (obraId) {
    threadsQuery = threadsQuery.eq('obra_id', obraId);
  }

  const { data: threadRows, error: threadsError } = await threadsQuery;

  if (threadsError) {
    throw threadsError;
  }

  const threads = Array.isArray(threadRows) ? threadRows : [];
  const threadIds = threads
    .map((row: any) => row?.id)
    .filter((id: string | undefined | null): id is string => typeof id === 'string' && id.length > 0);

  if (!threadIds.length) {
    return { unreadThreadIds: [] as string[], mentionThreadIds: [] as string[] };
  }

  const { data: readRows, error: readsError } = await supabase
    .from('app_thread_reads')
    .select('thread_id,last_read_at')
    .eq('usuario_id', usuarioId)
    .in('thread_id', threadIds);

  if (readsError) {
    console.error('[notificationsService] getUnreadThreadsForUser -> reads error', readsError);
  }

  const readMap = new Map<string, number>();
  (readRows ?? []).forEach((row: any) => {
    const threadId = row?.thread_id;
    if (!threadId) return;
    const readAt = row?.last_read_at ? new Date(row.last_read_at).getTime() : 0;
    const prev = readMap.get(threadId) ?? 0;
    if (readAt > prev) {
      readMap.set(threadId, readAt);
    }
  });

  const unreadThreadIds = threads
    .filter((thread: any) => {
      const id = thread?.id;
      if (!id) return false;
      const lastAt = thread?.last_at ? new Date(thread.last_at).getTime() : 0;
      if (!lastAt) return false;
      const readAt = readMap.get(id) ?? 0;
      return lastAt > readAt;
    })
    .map((thread: any) => thread.id as string);

  if (!includeMentions || !unreadThreadIds.length) {
    return { unreadThreadIds, mentionThreadIds: [] as string[] };
  }

  const mentionFilterLiteral = buildUuidArrayFilter([usuarioId]);

  const { data: mentionRows, error: mentionError } = await supabase
    .from('app_comments')
    .select('thread_id')
    .in('thread_id', unreadThreadIds)
    .filter('mencoes', 'cs', mentionFilterLiteral)
    .is('deleted_at', null);

  if (mentionError) {
    console.error('[notificationsService] getUnreadThreadsForUser -> mentions error', mentionError);
    return { unreadThreadIds, mentionThreadIds: [] as string[] };
  }

  const mentionThreadIds = Array.from(
    new Set(
      (mentionRows ?? [])
        .map((row: any) => row?.thread_id)
        .filter((id: string | undefined | null): id is string => typeof id === 'string' && id.length > 0),
    ),
  );

  return { unreadThreadIds, mentionThreadIds };
}

let recurringTimer: number | null = null;

type RecurringOptions = {
  onTick?: () => void | Promise<void>;
  intervalMs?: number;
};

export function scheduleRecurringChecks(
  cbOrOpts?: (() => void | Promise<void>) | RecurringOptions,
  maybeIntervalMs?: number,
): number {
  if (typeof window === 'undefined') return -1 as unknown as number;
  if (recurringTimer) {
    clearInterval(recurringTimer);
    recurringTimer = null;
  }

  let onTick: (() => void | Promise<void>) | undefined;
  let intervalMs = 60_000;

  if (typeof cbOrOpts === 'function') {
    onTick = cbOrOpts;
    if (typeof maybeIntervalMs === 'number' && Number.isFinite(maybeIntervalMs)) {
      intervalMs = maybeIntervalMs;
    }
  } else if (cbOrOpts && typeof cbOrOpts === 'object') {
    onTick = cbOrOpts.onTick;
    if (typeof cbOrOpts.intervalMs === 'number' && Number.isFinite(cbOrOpts.intervalMs)) {
      intervalMs = cbOrOpts.intervalMs;
    }
  }

  if (!onTick) {
    onTick = async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (user?.id) {
          await getUnreadCountGlobal(user.id);
        }
      } catch {}
    };
  }

  const runner = () => {
    try {
      if (typeof document !== 'undefined' && 'visibilityState' in document && document.visibilityState !== 'visible') {
        return;
      }
      const result = onTick?.();
      if (result && typeof (result as Promise<unknown>).then === 'function') {
        (result as Promise<unknown>).catch(() => {});
      }
    } catch {}
  };

  recurringTimer = window.setInterval(runner, intervalMs);
  return recurringTimer;
}

export function cancelRecurringChecks(id?: number) {
  if (typeof window === 'undefined') return;
  if (typeof id === 'number') {
    clearInterval(id);
    if (recurringTimer === id) recurringTimer = null;
    return;
  }
  if (recurringTimer) {
    clearInterval(recurringTimer);
    recurringTimer = null;
  }
}

export async function criarNotificacoesMencao(params: {
  usuariosIds: string[];
  comentario: {
    id: string;
    conteudo: string;
    tarefa_id?: string | null;
    created_by: string;
    thread_id?: string | null;
  };
  tarefa?: {
    id?: string | null;
    nome?: string | null;
    obra_id?: string | null;
  };
  autorNome?: string | null;
}) {
  const { usuariosIds, comentario, tarefa, autorNome } = params || {};
  if (!usuariosIds?.length) return [] as any[];

  const tituloBase =
    (tarefa?.nome && `Você foi mencionado em ${tarefa.nome}`) || 'Você foi mencionado';
  const mensagem = comentario?.conteudo ?? '';

  const rows = usuariosIds.map((uid) => ({
    tipo: 'mencao',
    titulo: tituloBase,
    mensagem,
    dados: {
      thread_id: comentario?.thread_id ?? null,
      comentario_id: comentario?.id ?? null,
      tarefa_id: tarefa?.id ?? comentario?.tarefa_id ?? null,
      obra_id: tarefa?.obra_id ?? null,
      autor_nome: autorNome ?? null,
    },
    lida: false,
    usuario_id: uid,
    tarefa_id: tarefa?.id ?? comentario?.tarefa_id ?? null,
    obra_id: tarefa?.obra_id ?? null,
    comentario_id: comentario?.id ?? null,
    url_acao: tarefa?.id ? `/tarefas/${tarefa.id}?comment=${comentario?.id}` : null,
  }));

  const { data, error } = await supabase.from(NOTIFICATIONS_TABLE).insert(rows).select('*');
  if (error) {
    console.error('[notificationsService] criarNotificacoesMencao', error);
    throw error;
  }
  return data ?? [];
}

export async function excluirNotificacao(notificacaoId: string) {
  const { error } = await supabase
    .from(NOTIFICATIONS_TABLE)
    .delete()
    .eq('id', notificacaoId);

  if (error) {
    console.error('[notificationsService] excluirNotificacao', error);
    throw error;
  }
}

export async function buscarNotificacoesUsuario(
  usuarioId: string,
  opts: { limite?: number; apenasNaoLidas?: boolean } = {},
) {
  const { limite = 10, apenasNaoLidas = false } = opts;

  let query = supabase
    .from(NOTIFICATIONS_TABLE)
    .select('*')
    .eq('usuario_id', usuarioId)
    .order('created_at', { ascending: false })
    .limit(limite);

  if (apenasNaoLidas) {
    query = query.eq('lida', false);
  }

  const { data, error } = await query;
  if (error) {
    console.error('[notificationsService] buscarNotificacoesUsuario', error);
    return [];
  }
  return data ?? [];
}

export async function marcarComoLida(notificacaoId: string) {
  const { error } = await supabase
    .from(NOTIFICATIONS_TABLE)
    .update({ lida: true })
    .eq('id', notificacaoId);

  if (error) {
    console.error('[notificationsService] marcarComoLida', error);
    throw error;
  }
}

export async function marcarTodasComoLidas(usuarioId: string) {
  const { error } = await supabase
    .from(NOTIFICATIONS_TABLE)
    .update({ lida: true })
    .eq('usuario_id', usuarioId)
    .eq('lida', false);

  if (error) {
    console.error('[notificationsService] marcarTodasComoLidas', error);
    throw error;
  }
}

